import React, { useEffect, useRef } from 'react'

import { AppFooter } from '../layout/footer/footer.component'
import { AppHeader } from '../layout/header/header.component'
import { cookiePolicyContent } from './cookie-policy.constants'
import styles from './cookie-policy-page.module.css'

interface OneTrustGlobal {
  Optanon?: {
    InsertHtml: () => void
  }
}

interface CookiePolicyContent {
  title: string
  introduction?: string
  sections: Array<{
    title: string
    paragraphs?: string[]
    list?: Array<{ title: string; description: string }>
    showOneTrustList?: boolean
  }>
  contact?: string
}

interface CookiePolicyPageProps {
  content?: CookiePolicyContent
  showOneTrustList?: boolean
}

export const CookiePolicyPage: React.FC<CookiePolicyPageProps> = ({
  content = cookiePolicyContent,
  showOneTrustList = true,
}) => {
  const cookieListRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const loadOneTrustContent = () => {
      const oneTrustDiv = document.getElementById('ot-sdk-cookie-policy')

      // Check if OneTrust div exists and has been populated with content
      if (
        oneTrustDiv &&
        cookieListRef.current &&
        oneTrustDiv.children.length > 0
      ) {
        // Clone the content from the OneTrust div
        const clonedContent = oneTrustDiv.cloneNode(true) as HTMLElement
        clonedContent.style.display = 'block'
        clonedContent.id = 'ot-sdk-cookie-policy-display' // Change ID to avoid conflicts

        // Clear any existing content and append the cloned OneTrust content
        cookieListRef.current.innerHTML = ''
        cookieListRef.current.appendChild(clonedContent)

        return true
      }

      return false
    }

    // Try to load immediately
    if (loadOneTrustContent()) {
      return
    }

    // If not loaded yet, poll until OneTrust content is available
    const maxAttempts = 20
    let attempts = 0

    const interval = setInterval(() => {
      attempts++

      if (loadOneTrustContent() || attempts >= maxAttempts) {
        clearInterval(interval)
      }
    }, 200) // Check every 200ms

    // Trigger OneTrust to populate if available
    if ((globalThis as unknown as OneTrustGlobal).Optanon?.InsertHtml) {
      ;(globalThis as unknown as OneTrustGlobal).Optanon!.InsertHtml()
    }

    return () => clearInterval(interval)
  }, [])

  return (
    <div className="atls">
      <AppHeader />
      <div className={styles.container}>
        <div className={styles.content}>
          <h1 className={styles.title}>{content.title}</h1>

          {content.introduction && (
            <p
              className={styles.introduction}
              dangerouslySetInnerHTML={{
                __html: content.introduction,
              }}
            />
          )}

          {content.sections.map((section) => (
            <section key={section.title} className={styles.section}>
              <h2 className={styles.sectionTitle}>{section.title}</h2>
              {section.paragraphs?.map((paragraph, pIndex) => (
                <p
                  key={`${section.title}-paragraph-${pIndex}`}
                  className={styles.paragraph}
                  dangerouslySetInnerHTML={{ __html: paragraph }}
                />
              ))}
              {section.list && (
                <ul className={styles.list}>
                  {section.list.map((item, lIndex) => (
                    <li key={`${section.title}-list-${lIndex}`}>
                      <strong>{item.title}:</strong> {item.description}
                    </li>
                  ))}
                </ul>
              )}
              {section.showOneTrustList && showOneTrustList && (
                <div
                  ref={cookieListRef}
                  className={styles.oneTrustContainer}
                ></div>
              )}
            </section>
          ))}

          {content.contact && (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Contact Us</h2>
              <p
                className={styles.paragraph}
                dangerouslySetInnerHTML={{
                  __html: content.contact,
                }}
              />
            </section>
          )}
        </div>
      </div>
      <AppFooter />
    </div>
  )
}
